import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, ViewEncapsulation } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { normalizeToastMessage } from 'src/shared/util/util';

@Component({
    selector: 'slider-trigger',
    templateUrl: 'slider-trigger.html',
    styleUrls: ['./slider-trigger.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SliderTriggerComponent {

  @Input() label: string;
  @Input() onTriggeredAction = null;

  value = 0;
  disabled = false;
  loading = false;

  private dragging = false;
  private trackWidth = 0;
  private thumbSize = 48;
  private padding = 4;
  private startX = 0;

  constructor(private el: ElementRef, private zone: NgZone, private cdr: ChangeDetectorRef, private toastCtrl: ToastController) {}

  get thumbLeft(): string {
    // Thumb center goes from (padding + thumbSize/2) to (trackWidth - padding - thumbSize/2)
    // Using calc: from 28px to calc(100% - 28px)
    const offset = this.padding + this.thumbSize / 2;
    return `calc(${offset}px + ${this.value * 100}% - ${this.value * offset * 2}px)`;
  }

  get fillPercent(): number {
    // Fill should cover from left edge to thumb center
    return this.value * 100;
  }

  onMultiClick(): void {
    if (this.value !== 1) {
      this.value = 1;
      this.executeAction();
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (this.disabled || this.loading) return;
    event.preventDefault();
    const touch = event.touches[0];
    this.startDrag(touch.clientX);

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      // Native listeners added on `document` can fire outside Angular's zone on
      // iOS, leaving the bound thumb/fill styles stale (stuck mid-path). Run the
      // update inside the zone so change detection repaints. See Issue 10.
      this.zone.run(() => this.updateDrag(e.touches[0].clientX));
    };
    const onTouchEnd = () => {
      this.zone.run(() => this.endDrag());
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }

  onMouseDown(event: MouseEvent): void {
    if (this.disabled || this.loading) return;
    this.startDrag(event.clientX);

    const onMouseMove = (e: MouseEvent) => this.zone.run(() => this.updateDrag(e.clientX));
    const onMouseUp = () => {
      this.zone.run(() => this.endDrag());
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private startDrag(clientX: number): void {
    const track = this.el.nativeElement.querySelector('.slider-trigger-track');
    if (!track) return;
    this.trackWidth = track.getBoundingClientRect().width;
    this.startX = track.getBoundingClientRect().left;
    this.dragging = true;
    this.updateDrag(clientX);
  }

  private updateDrag(clientX: number): void {
    if (!this.dragging || this.trackWidth === 0) return;
    const usableWidth = this.trackWidth - this.thumbSize - this.padding * 2;
    const offset = clientX - this.startX - this.padding - this.thumbSize / 2;
    this.value = Math.min(1, Math.max(0, offset / usableWidth));
  }

  private endDrag(): void {
    if (!this.dragging) return;
    this.dragging = false;

    if (this.value >= 0.95) {
      this.value = 1;
      this.executeAction();
    } else {
      this.animateBack();
    }
  }

  async executeAction() {
    if (this.loading || this.onTriggeredAction == null) {
      return;
    }

    try {
      this.disabled = true;
      this.loading = true;
      await this.onTriggeredAction();
    } catch (error) {
      // Same as tidy-button, the desktop control for these actions: show the
      // error instead of leaving the rejection unhandled.
      await this.showError(error);
    } finally {
      this.reset();
    }
  }

  private async showError(error): Promise<void> {
    const message = normalizeToastMessage(error);
    if (!message) {
      return;
    }
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration: 5000,
      cssClass: 'toast-error'
    });
    await toast.present();
  }

  public reset(): void {
    this.value = 0;
    this.loading = false;
    this.disabled = false;
    this.cdr.detectChanges();
  }

  private animateBack(): void {
    // requestAnimationFrame callbacks run outside Angular's zone, so without an
    // explicit detectChanges the thumb stays frozen mid-path on iOS instead of
    // sliding back to the start. See Issue 10.
    const step = () => {
      if (this.value > 0) {
        this.value = Math.max(0, this.value - 0.05);
        this.cdr.detectChanges();
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }
}
